import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { collections } from '../../mocks/collections';

export default function Home() {
  const [isVisible, setIsVisible] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const navigate = useNavigate();
  const sectionRefs = useRef<(HTMLElement | null)[]>([]);
  const [visibleSections, setVisibleSections] = useState<Set<number>>(new Set());

  useEffect(() => {
    setIsVisible(true);

    const handleScroll = () => {
      const scrolled = window.scrollY;
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (scrolled / maxScroll) * 100;
      setScrollProgress(progress);

      // Intersection observer for sections
      sectionRefs.current.forEach((section, index) => {
        if (section) {
          const rect = section.getBoundingClientRect();
          const isInView = rect.top < window.innerHeight * 0.8 && rect.bottom > 0;
          
          if (isInView) {
            setVisibleSections(prev => new Set(prev).add(index));
          }
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleCollectionClick = (collectionName: string) => {
    navigate('/contact', { state: { collection: collectionName } });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Scroll Progress Bar */}
      <div className="fixed top-0 left-0 right-0 h-1 bg-gray-200/30 z-50">
        <div 
          className="h-full bg-gradient-to-r from-[#C9A961] to-[#D4B76A] transition-all duration-300"
          style={{ width: `${scrollProgress}%` }}
        ></div>
      </div>

      <Navbar />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/15 to-black/25"></div>
        <div 
          className="absolute inset-0 bg-cover bg-center transition-transform duration-[3000ms] ease-out"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/80ab379248d3538ef6018d91c44391ee.jpg')`,
            objectPosition: 'center',
            transform: `scale(${1 + scrollProgress * 0.001})`
          }}
        ></div>
        
        <div className={`relative z-10 text-center px-6 max-w-5xl w-full transition-all duration-1000 transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <p className="text-sm font-light text-white/90 tracking-[0.3em] mb-8 uppercase animate-fadeIn">Timeless Elegance</p>
          <h2 className="text-6xl md:text-8xl font-light mb-8 text-white tracking-wide leading-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Where Light<br />Meets Grace
          </h2>
          <p className="text-lg md:text-xl font-light text-white/90 mb-14 max-w-2xl mx-auto leading-relaxed">
            Each piece tells a story of refined beauty and timeless sophistication
          </p>
          <button 
            onClick={() => navigate('/contact')}
            className="group px-12 py-5 bg-white text-[#4A4A4A] text-sm font-light tracking-[0.2em] hover:bg-[#C9A961] hover:text-white transition-all duration-500 whitespace-nowrap cursor-pointer rounded-full shadow-lg hover:shadow-2xl transform hover:scale-105 relative overflow-hidden"
          >
            <span className="relative z-10">DISCOVER YOUR STORY</span>
            <div className="absolute inset-0 bg-gradient-to-r from-[#C9A961] to-[#D4B76A] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
            <span className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500 text-white z-20">DISCOVER YOUR STORY</span>
          </button>
        </div>
      </section>

      {/* Brand Story Introduction */}
      <section 
        ref={el => sectionRefs.current[0] = el}
        className={`py-40 px-6 bg-white transition-all duration-1000 ${visibleSections.has(0) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
      >
        <div className="max-w-5xl mx-auto text-center">
          <div className="w-16 h-[1px] bg-[#C9A961] mx-auto mb-12 transform scale-x-0 animate-expandWidth"></div>
          <h3 className="text-5xl md:text-6xl font-light mb-12 text-[#4A4A4A] leading-tight hover:text-[#C9A961] transition-colors duration-500" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            The Art of Subtlety
          </h3>
          <p className="text-xl font-light text-[#6B6B6B] leading-loose mb-8 max-w-3xl mx-auto">
            In a world of excess, we choose restraint. In a time of noise, we whisper elegance.
          </p>
          <p className="text-lg font-light text-[#8B8B8B] leading-loose max-w-3xl mx-auto">
            LUMIÈRE was born from a simple belief: that true luxury doesn't shout—it glows. Each piece is a testament to the quiet confidence of contemporary women who understand that less is infinitely more.
          </p>
        </div>
      </section>

      {/* Visual Story Section */}
      <section 
        ref={el => sectionRefs.current[1] = el}
        className="py-20 px-6 bg-[#FAF8F5]"
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="h-[700px] rounded-2xl overflow-hidden group">
              <img 
                src="https://readdy.ai/api/search-image?query=minimalist%20luxury%20gold%20jewelry%20delicate%20bracelet%20on%20elegant%20woman%20wrist%20holding%20white%20coffee%20cup%20morning%20light%20soft%20beige%20cream%20background%20lifestyle%20photography%20sophisticated%20aesthetic%20clean%20composition%20high%20end%20brand%20storytelling%20natural%20moment%20refined%20elegance&width=800&height=1100&seq=story001luxclean&orientation=portrait"
                alt="Morning Elegance"
                className="w-full h-full object-cover object-top transition-all duration-700 group-hover:scale-110 group-hover:rotate-1"
              />
            </div>
            <div className="flex flex-col gap-4">
              <div className="h-[340px] rounded-2xl overflow-hidden group">
                <img 
                  src="https://readdy.ai/api/search-image?query=close%20up%20minimalist%20gold%20earrings%20on%20elegant%20woman%20ear%20soft%20focus%20warm%20beige%20cream%20background%20natural%20daylight%20luxury%20fine%20jewelry%20detail%20photography%20sophisticated%20aesthetic%20clean%20composition%20high%20end%20brand%20visual%20gentle%20shadows&width=800&height=540&seq=story002luxclean&orientation=landscape"
                  alt="Detail Perfection"
                  className="w-full h-full object-cover object-top transition-all duration-700 group-hover:scale-110 group-hover:brightness-110"
                />
              </div>
              <div className="h-[340px] rounded-2xl overflow-hidden group">
                <img 
                  src="https://readdy.ai/api/search-image?query=minimalist%20gold%20necklace%20displayed%20on%20white%20silk%20fabric%20with%20soft%20folds%20warm%20beige%20cream%20tones%20gentle%20natural%20light%20creating%20subtle%20shadows%20luxury%20fine%20jewelry%20product%20photography%20sophisticated%20clean%20aesthetic%20high%20end%20brand%20visual%20empty%20space%20refined%20composition&width=800&height=540&seq=story003luxclean&orientation=landscape"
                  alt="Silk and Gold"
                  className="w-full h-full object-cover object-top transition-all duration-700 group-hover:scale-110 group-hover:brightness-110"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section 
        ref={el => sectionRefs.current[2] = el}
        className={`py-40 px-6 bg-white transition-all duration-1000 ${visibleSections.has(2) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
      >
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
          <div>
            <div className="w-12 h-[1px] bg-[#C9A961] mb-8 transform origin-left scale-x-0 animate-expandWidth"></div>
            <h3 className="text-5xl md:text-6xl font-light mb-10 text-[#4A4A4A] leading-tight hover:text-[#C9A961] transition-colors duration-500" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Designed for<br />Every Chapter
            </h3>
            <p className="text-lg font-light text-[#6B6B6B] leading-loose mb-8">
              From the quiet confidence of morning coffee to the radiant energy of evening celebrations, our jewelry moves with you through life's beautiful moments.
            </p>
            <p className="text-lg font-light text-[#6B6B6B] leading-loose mb-8">
              Each piece is crafted to be worn, loved, and lived in—not hidden away. This is jewelry for the woman who knows her worth and wears it with grace.
            </p>
            <button 
              onClick={() => navigate('/about')}
              className="text-sm font-light text-[#C9A961] tracking-[0.2em] hover:tracking-[0.3em] transition-all duration-300 whitespace-nowrap cursor-pointer flex items-center gap-3 group"
            >
              OUR STORY
              <i className="ri-arrow-right-line transform group-hover:translate-x-2 transition-transform duration-300"></i>
            </button>
          </div>
          
          <div className="w-full h-[650px] rounded-2xl overflow-hidden group">
            <img 
              src="https://readdy.ai/api/search-image?query=elegant%20woman%20wearing%20minimalist%20gold%20jewelry%20delicate%20necklace%20and%20earrings%20natural%20portrait%20soft%20warm%20beige%20cream%20background%20gentle%20daylight%20sophisticated%20lifestyle%20photography%20high%20end%20fashion%20aesthetic%20clean%20composition%20refined%20beauty%20contemporary%20elegance&width=800&height=1000&seq=philosophy002luxclean&orientation=portrait"
              alt="Contemporary Elegance"
              className="w-full h-full object-cover object-top transition-all duration-700 group-hover:scale-105"
            />
          </div>
        </div>
      </section>

      {/* Featured Collections */}
      <section 
        ref={el => sectionRefs.current[3] = el}
        className="py-40 px-6 bg-[#FAF8F5]"
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-24">
            <div className="w-16 h-[1px] bg-[#C9A961] mx-auto mb-12"></div>
            <h3 className="text-5xl md:text-6xl font-light text-[#4A4A4A] mb-6 hover:text-[#C9A961] transition-colors duration-500" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Signature Collections
            </h3>
            <p className="text-lg font-light text-[#8B8B8B] max-w-2xl mx-auto">
              Three distinct expressions of timeless beauty
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {collections.map((collection, index) => (
              <div 
                key={collection.id}
                className={`group cursor-pointer transition-all duration-700 transform hover:-translate-y-2 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'} ${
                  index === 0 ? 'lg:col-span-2 lg:row-span-2' : ''
                }`}
                style={{ transitionDelay: `${index * 150}ms` }}
                onClick={() => handleCollectionClick(collection.name)}
              >
                <div className={`relative overflow-hidden rounded-2xl mb-8 w-full ${
                  index === 0 ? 'h-[700px]' : 'h-[340px]'
                }`}>
                  <img 
                    src={collection.image}
                    alt={collection.name}
                    className="w-full h-full object-cover object-top transition-all duration-700 group-hover:scale-110 group-hover:brightness-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  <div className="absolute bottom-8 left-8 right-8 transform translate-y-8 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
                    <p className="text-white text-sm font-light">Click to explore</p>
                  </div>
                </div>
                <h4 className="text-3xl font-light text-[#4A4A4A] mb-4 group-hover:text-[#C9A961] transition-colors duration-300" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  {collection.name}
                </h4>
                <p className="text-base font-light text-[#6B6B6B] leading-relaxed mb-6">
                  {collection.description}
                </p>
                <button 
                  className="text-sm font-light text-[#C9A961] tracking-[0.2em] hover:tracking-[0.3em] transition-all duration-300 whitespace-nowrap cursor-pointer flex items-center gap-3 group"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCollectionClick(collection.name);
                  }}
                >
                  EXPLORE
                  <i className="ri-arrow-right-line transform group-hover:translate-x-2 transition-transform duration-300"></i>
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Craftsmanship Story */}
      <section 
        ref={el => sectionRefs.current[4] = el}
        className={`py-40 px-6 bg-white transition-all duration-1000 ${visibleSections.has(4) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
      >
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
          <div className="w-full h-[650px] rounded-2xl overflow-hidden group">
            <img 
              src="https://readdy.ai/api/search-image?query=artisan%20hands%20carefully%20crafting%20delicate%20gold%20jewelry%20piece%20on%20clean%20white%20workbench%20warm%20natural%20lighting%20close%20up%20detail%20shot%20showing%20precision%20and%20care%20luxury%20craftsmanship%20photography%20sophisticated%20aesthetic%20fine%20jewelry%20making%20process%20high%20end%20brand%20storytelling%20minimal%20background&width=800&height=1000&seq=craft002luxclean&orientation=portrait"
              alt="Artisan Craftsmanship"
              className="w-full h-full object-cover object-top transition-all duration-700 group-hover:scale-105"
            />
          </div>
          
          <div>
            <div className="w-12 h-[1px] bg-[#C9A961] mb-8"></div>
            <h3 className="text-5xl md:text-6xl font-light mb-10 text-[#4A4A4A] leading-tight hover:text-[#C9A961] transition-colors duration-500" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Crafted with<br />Intention
            </h3>
            <p className="text-lg font-light text-[#6B6B6B] leading-loose mb-8">
              Behind every piece lies hours of meticulous work by master artisans who have dedicated their lives to the art of jewelry making.
            </p>
            <p className="text-lg font-light text-[#6B6B6B] leading-loose mb-8">
              We use only ethically sourced materials and time-honored techniques, ensuring that each creation is not just beautiful, but meaningful.
            </p>
            <div className="grid grid-cols-2 gap-8 mt-12">
              <div className="group cursor-default">
                <p className="text-4xl font-light text-[#C9A961] mb-3 group-hover:scale-110 transition-transform duration-300" style={{ fontFamily: 'Cormorant Garamond, serif' }}>100%</p>
                <p className="text-sm font-light text-[#8B8B8B] tracking-wider">Handcrafted</p>
              </div>
              <div className="group cursor-default">
                <p className="text-4xl font-light text-[#C9A961] mb-3 group-hover:scale-110 transition-transform duration-300" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Ethical</p>
                <p className="text-sm font-light text-[#8B8B8B] tracking-wider">Sourcing</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section 
        ref={el => sectionRefs.current[5] = el}
        className={`py-40 px-6 bg-[#FAF8F5] transition-all duration-1000 ${visibleSections.has(5) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
      >
        <div className="max-w-5xl mx-auto text-center">
          <div className="w-16 h-[1px] bg-[#C9A961] mx-auto mb-12"></div>
          <h3 className="text-5xl md:text-6xl font-light mb-10 text-[#4A4A4A] leading-tight hover:text-[#C9A961] transition-colors duration-500" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Your Personal Journey
          </h3>
          <p className="text-xl font-light text-[#6B6B6B] leading-loose mb-16 max-w-3xl mx-auto">
            We believe that choosing jewelry should be as special as wearing it. Book a private appointment and let us guide you through our collections in an intimate, unhurried setting.
          </p>
          <button 
            onClick={() => navigate('/contact')}
            className="group px-12 py-5 bg-[#C9A961] text-white text-sm font-light tracking-[0.2em] hover:bg-[#B89851] transition-all duration-500 whitespace-nowrap cursor-pointer rounded-full shadow-lg hover:shadow-2xl transform hover:scale-105 relative overflow-hidden"
          >
            <span className="relative z-10">BOOK YOUR VISIT</span>
          </button>
        </div>
      </section>

      {/* Values Section */}
      <section 
        ref={el => sectionRefs.current[6] = el}
        className="py-40 px-6 bg-white"
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-24">
            <div className="w-16 h-[1px] bg-[#C9A961] mx-auto mb-12"></div>
            <h3 className="text-5xl md:text-6xl font-light text-[#4A4A4A] hover:text-[#C9A961] transition-colors duration-500" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Our Commitments
            </h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-16">
            <div className="text-center group cursor-default">
              <div className="w-20 h-20 flex items-center justify-center mx-auto mb-8 text-[#C9A961] transform group-hover:scale-110 group-hover:rotate-12 transition-all duration-500">
                <i className="ri-leaf-line text-5xl"></i>
              </div>
              <h4 className="text-2xl font-light text-[#4A4A4A] mb-4 group-hover:text-[#C9A961] transition-colors duration-300" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Sustainable
              </h4>
              <p className="text-base font-light text-[#6B6B6B] leading-relaxed">
                Every material is ethically sourced and every process is designed to minimize our environmental impact.
              </p>
            </div>
            
            <div className="text-center group cursor-default">
              <div className="w-20 h-20 flex items-center justify-center mx-auto mb-8 text-[#C9A961] transform group-hover:scale-110 group-hover:rotate-12 transition-all duration-500">
                <i className="ri-heart-line text-5xl"></i>
              </div>
              <h4 className="text-2xl font-light text-[#4A4A4A] mb-4 group-hover:text-[#C9A961] transition-colors duration-300" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Timeless
              </h4>
              <p className="text-base font-light text-[#6B6B6B] leading-relaxed">
                We create pieces that transcend trends, designed to be cherished for generations to come.
              </p>
            </div>
            
            <div className="text-center group cursor-default">
              <div className="w-20 h-20 flex items-center justify-center mx-auto mb-8 text-[#C9A961] transform group-hover:scale-110 group-hover:rotate-12 transition-all duration-500">
                <i className="ri-star-line text-5xl"></i>
              </div>
              <h4 className="text-2xl font-light text-[#4A4A4A] mb-4 group-hover:text-[#C9A961] transition-colors duration-300" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Exceptional
              </h4>
              <p className="text-base font-light text-[#6B6B6B] leading-relaxed">
                From the first sketch to the final polish, we pursue perfection in every detail.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500&display=swap');
        
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes expandWidth {
          from {
            transform: scaleX(0);
          }
          to {
            transform: scaleX(1);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 1.5s ease-out;
        }

        .animate-expandWidth {
          animation: expandWidth 1s ease-out 0.5s forwards;
        }
      `}</style>
    </div>
  );
}
